import { FontAwesome, SimpleLineIcons, Zocial } from "@expo/vector-icons";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useNavigation, useRoute } from "@react-navigation/core";
import PropTypes from "prop-types";
import React, { memo, useEffect, useRef, useState } from "react";
import {
	Image,
	Keyboard,
	KeyboardAvoidingView,
	LayoutAnimation,
	Platform,
	StyleSheet,
	Text,
	TouchableOpacity,
	View
} from "react-native";
import { Button, CheckBox, Input, Tooltip } from "react-native-elements";
import { SafeAreaView } from "react-native-safe-area-context";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
	fetchForgetPassword,
	fetchLogin,
	fetchLoginAsGuest,
	fetchLoginUsingToken,
	fetchLoginWithApple,
	fetchLoginWithGoogle,
	fetchLoginWithFB,
	fetchSignUp,
	fetchSignupAsGuest,
	fetchSignUpWithApple,
	fetchSignUpWithGoogle,
	fetchSignUpWithFB
} from "../actions/login";
import { fetchGetCommunityDetails } from "../actions/community";
import {
	CLEAR_FORGET_PWD_VALIDATE,
	CLEAR_LOGIN,
	CLEAR_VALIDATE,
	CLEAR_VALIDATE_APPLE,
	CLEAR_VALIDATE_GUEST,
	CLEAR_WRONG_NEIGHBORHOOD_SUGGESTION,
	CLEAR_REGISTRATION_BLOCKED_BY_COMMUNITY,
	LOGOUT,
	SET_DROP_DOWN_ALERT_ERROR,
	SET_DROP_DOWN_ALERT_WARN,
	SET_EMAIL_OR_PSEUDO,
	SET_PASSWORD,
	SET_REGISTER_KEY,
	SET_SELECTED_CATEGORY,
	SET_TOOLTIP3_VISIBLE,
	SET_TOOLTIP4_VISIBLE,
	SET_TOOLTIP5_VISIBLE,
	SET_TOOLTIP6_VISIBLE,
	VALIDATE_APPLE_INPUTS,
	VALIDATE_FORGET_PWD_INPUT,
	VALIDATE_GUEST_INPUT,
	VALIDATE_INPUTS,
	TOGGLE_CGU_CHECKED
} from "../actions/types";
import { getImage } from "../assets/Images";
import { getLoginSelector } from "../selectors";
import { t } from "../services/i18n";
import commonStyles, {
	COLOR2,
	DARKGREY_COLOR,
	isAndroid,
	isElectron,
	isiOS,
	isWeb,
	SCREEN_WIDTH,
	WHITE_COLOR
} from "../styles/commonStyles";
import { signInWithApple, isAppleCancel } from "../services/appleWebAuth";
import { signInWithGoogle, isGoogleCancel } from "../services/googleWebAuth";
import {
	signInWithFacebook,
	isFacebookCancel
} from "../services/facebookWebAuth";
import { GoogleIcon, AppleIcon, FacebookIcon } from "./SocialIcons";
import ConfirmationModal from "./ConfirmationModal";
import SelectionGuide from "./SelectionGuide";

let majorVersionIOS = 0;
if (isiOS) {
	majorVersionIOS = parseInt(Platform.Version, 10);
}

// Uniform circular social-login button: white disc, thin border, subtle shadow.
const socialCircleStyle = {
	height: 54,
	width: 54,
	borderRadius: 27,
	marginHorizontal: 8,
	backgroundColor: WHITE_COLOR,
	borderWidth: 1,
	borderColor: "#e0e0e0",
	alignItems: "center",
	justifyContent: "center",
	shadowColor: "#000",
	shadowOpacity: 0.08,
	shadowRadius: 3,
	shadowOffset: { width: 0, height: 1 },
	elevation: 2
};

const TabSelector = ({ selected }) => {
	const { flex1, alignItemsCenter } = commonStyles;
	return (
		<View style={[flex1, alignItemsCenter]}>
			<View style={selected && styles.selected} />
		</View>
	);
};

TabSelector.propTypes = {
	selected: PropTypes.bool.isRequired
};

const Login = () => {
	const dispatch = useDispatch();
	const navigation = useNavigation();
	const route = useRoute();

	const tooltip3Ref = useRef();
	const tooltip4Ref = useRef();
	const tooltip6Ref = useRef();
	const emailOrPseudoInputRef = useRef();
	const passwordInputRef = useRef();
	const registerkeyInputRef = useRef();
	// Anchors of the signup guide (social login or email)
	const socialAnchorRef = useRef();
	const emailAnchorRef = useRef();
	const [showSignupGuide, setShowSignupGuide] = useState(false);
	const signupGuideShownRef = useRef(false);
	const wrongNeighborhoodModalRef = useRef();
	const registrationBlockedModalRef = useRef();
	// Capture the latest login attempt (strategy + credentials) so the
	// wrong-neighborhood modal can re-run the same flow on the user's actual
	// community. Without this, a user who signed in via Google/Apple/Facebook
	// would have the retry fall back to a (never-typed) local email/password
	// and get "Invalid email or password". Discriminated by `kind`:
	//   { kind: "local", emailOrPseudo, password }
	//   { kind: "google", credential }
	//   { kind: "facebook", token }
	//   { kind: "apple", credential }
	const lastLoginAttemptRef = useRef(null);
	const { token, communityUri, registerkey: registerkeyParam } = route.params;

	const {
		isFetching,
		emailOrPseudo,
		emailOrPseudoErrorMsg,
		password,
		passwordErrorMsg,
		valid,
		clearValidate,
		registerkey,
		registerkeyErrorMsg,
		clearValidateApple,
		validApple,
		clearForgetPwdValidate,
		validForgetPwd,
		selectedCategory,
		tokenErrorMsg,
		isTooltip3Visible,
		isTooltip4Visible,
		isTooltip6Visible,
		clearValidateSignupGuest,
		validSignupGuest,
		fromLogin,
		lang,
		installationId,
		dropDownAlertInfo,
		dropDownAlertError,
		currentCommunity,
		canChangePassword,
		cguChecked,
		socialLogin,
		wrongNeighborhoodSuggestion,
		registrationBlockedByCommunity
	} = useSelector(getLoginSelector, shallowEqual);

	const [showImage, setShowImage] = useState(true);

	const {
		_id: communityId,
		logoUri,
		name: communityName,
		needRegisterKey,
		communitytype,
		isPrivate
	} = currentCommunity || {};
	const displayRegisterkey = needRegisterKey && !registerkeyParam;

	useEffect(() => {
		(async () => {
			const emailOrPseudo = await AsyncStorage.getItem("emailOrPseudo");
			if (emailOrPseudo) {
				dispatch({
					type: SET_EMAIL_OR_PSEUDO,
					value: emailOrPseudo
				});
			}
			const strategy = await AsyncStorage.getItem("strategy");
			if (!strategy && !token && !isPrivate) {
				dispatchSelectCategory(1);
			}
		})();

		const keyboardWillShowSub = Keyboard.addListener(
			isiOS ? "keyboardWillShow" : "keyboardDidShow",
			() => {
				LayoutAnimation.easeInEaseOut();
				setShowImage(false);
			}
		);
		const keyboardWillHideSub = Keyboard.addListener(
			isiOS ? "keyboardWillHide" : "keyboardDidHide",
			() => {
				LayoutAnimation.easeInEaseOut();
				setShowImage(true);
			}
		);
		return () => {
			keyboardWillShowSub.remove();
			keyboardWillHideSub.remove();
			dispatch({
				type: CLEAR_LOGIN
			});
		};
	}, []);

	useEffect(() => {
		if (registerkeyParam) {
			dispatch({
				type: SET_REGISTER_KEY,
				value: registerkeyParam
			});
		}
	}, [registerkeyParam]);

	useEffect(() => {
		if (selectedCategory) {
			showTooltips();
		}
	}, [selectedCategory]);

	useEffect(() => {
		const infos = "popup1|popup2";
		if (dropDownAlertInfo && infos.indexOf(dropDownAlertInfo)) {
			dispatch({
				type: SET_SELECTED_CATEGORY,
				value: 0
			});
		}
	}, [dropDownAlertInfo]);

	useEffect(() => {
		const errors = "unknown|unauthorized";

		if (dropDownAlertError && errors.indexOf(!dropDownAlertError)) {
			dispatch({
				type: SET_SELECTED_CATEGORY,
				value: 1
			});
		}
	}, [dropDownAlertError]);

	const getCommunityUri = () => {
		return currentCommunity
			? currentCommunity.customUri
				? currentCommunity.customUri
				: currentCommunity._id
			: "";
	};

	useEffect(() => {
		if (token) {
			dispatch({
				type: SET_SELECTED_CATEGORY,
				value: 0
			});
			if (communityUri === getCommunityUri()) {
				dispatch(fetchLoginUsingToken(token));
			}
		}
	}, [token]);

	useEffect(() => {
		// Validate email inputs
		if (clearValidate) {
			dispatch({
				type: VALIDATE_INPUTS
			});
		}
	}, [clearValidate]);

	useEffect(() => {
		// Validate Apple inputs
		if (clearValidateApple) {
			dispatch({
				type: VALIDATE_APPLE_INPUTS
			});
		}
	}, [clearValidateApple]);

	useEffect(() => {
		// Validate email inputs
		if (clearForgetPwdValidate) {
			dispatch({
				type: VALIDATE_FORGET_PWD_INPUT
			});
		}
	}, [clearForgetPwdValidate]);

	useEffect(() => {
		if (clearValidateSignupGuest) {
			dispatch({
				type: VALIDATE_GUEST_INPUT
			});
		}
	}, [clearValidateSignupGuest]);

	// HapiColibri: the server returned a "wrong neighborhood" rejection on
	// login, meaning the user picked the wrong community but is already
	// registered as NEIGHBOR in another one. Open the confirm modal to offer a
	// switch + auto-login.
	useEffect(() => {
		if (wrongNeighborhoodSuggestion) {
			wrongNeighborhoodModalRef.current?.open();
		} else {
			wrongNeighborhoodModalRef.current?.close();
		}
	}, [wrongNeighborhoodSuggestion]);

	// HapiColibri: signup is blocked because the user is already registered as
	// NEIGHBOR in another community. Inform them and offer to switch to the
	// login tab on their existing community so they can leave it manually.
	useEffect(() => {
		if (registrationBlockedByCommunity) {
			registrationBlockedModalRef.current?.open();
		} else {
			registrationBlockedModalRef.current?.close();
		}
	}, [registrationBlockedByCommunity]);

	useEffect(() => {
		if (passwordErrorMsg) {
			setTimeout(() => {
				passwordInputRef?.current?.shake();
				passwordInputRef?.current?.focus();
			}, 10);
		}
	}, [passwordErrorMsg]);

	useEffect(() => {
		if (registerkeyErrorMsg) {
			registerkeyInputRef?.current?.shake();
			registerkeyInputRef?.current?.focus();
		}
	}, [registerkeyErrorMsg]);

	useEffect(() => {
		if (emailOrPseudoErrorMsg) {
			emailOrPseudoInputRef?.current?.shake();
			emailOrPseudoInputRef?.current?.focus();
		}
	}, [emailOrPseudoErrorMsg]);

	useEffect(() => {
		if (valid) {
			if (isSignUpPage) {
				signUp();
			} else {
				login();
			}
		}
	}, [valid]);

	useEffect(() => {
		if (validApple) {
			logInWithApple();
		}
	}, [validApple]);

	useEffect(() => {
		if (validForgetPwd) {
			dispatch(fetchForgetPassword(communityId, emailOrPseudo, lang));
		}
	}, [validForgetPwd]);

	useEffect(() => {
		if (validSignupGuest) {
			signupAsGuest();
		}
	}, [validSignupGuest]);

	useEffect(() => {
		if (!isFetching && fromLogin && dropDownAlertError === "unexpectederror") {
			dispatch({
				type: LOGOUT
			});
		}
	}, [isFetching, fromLogin, dropDownAlertError]);

	useEffect(() => {
		if (isTooltip3Visible) {
			tooltip3Ref?.current?.toggleTooltip();
		}
	}, [isTooltip3Visible]);

	useEffect(() => {
		if (isTooltip4Visible) {
			tooltip4Ref?.current?.toggleTooltip();
		}
	}, [isTooltip4Visible]);

	useEffect(() => {
		if (isTooltip6Visible) {
			tooltip6Ref?.current?.toggleTooltip();
		}
	}, [isTooltip6Visible]);

	const loginAsGuest = () => {
		dispatch(
			fetchLoginAsGuest({
				communityId,
				deviceId: installationId
			})
		);
	};

	const signupAsGuest = () => {
		dispatch(
			fetchSignupAsGuest({
				communityId,
				deviceId: installationId,
				registerkey
			})
		);
	};

	const logInWithApple = async () => {
		try {
			// Web flow: opens the Apple popup (must be called from a user gesture).
			const credential = await signInWithApple();
			if (isSignUpPage) {
				dispatch(
					fetchSignUpWithApple(communityId, registerkey, lang, credential)
				);
			} else {
				lastLoginAttemptRef.current = { kind: "apple", credential };
				dispatch(fetchLoginWithApple(communityId, credential));
			}
		} catch (e) {
			if (isAppleCancel(e) || e?.code === "ERR_CANCELED") {
				// user canceled the sign-in flow
				dispatch({
					type: SET_DROP_DOWN_ALERT_WARN,
					warn: "applewarn"
				});
			} else {
				// handle other errors
				dispatch({
					type: SET_DROP_DOWN_ALERT_ERROR,
					error: "appleerror"
				});
			}
		}
	};

	const logInWithGoogle = async () => {
		try {
			// Web flow: opens the Google popup (must be called from a user gesture).
			const credential = await signInWithGoogle();
			if (isSignUpPage) {
				dispatch(
					fetchSignUpWithGoogle(communityId, registerkey, lang, credential)
				);
			} else {
				lastLoginAttemptRef.current = { kind: "google", credential };
				dispatch(fetchLoginWithGoogle(communityId, credential));
			}
		} catch (e) {
			if (isGoogleCancel(e)) {
				// user canceled the sign-in flow
				dispatch({
					type: SET_DROP_DOWN_ALERT_WARN,
					warn: "googlewarn"
				});
			} else {
				// handle other errors
				dispatch({
					type: SET_DROP_DOWN_ALERT_ERROR,
					error: "googleerror"
				});
			}
		}
	};

	const logInWithFB = async () => {
		try {
			// Web flow: opens the Facebook popup (must be called from a user gesture).
			const { token } = await signInWithFacebook();
			if (isSignUpPage) {
				dispatch(
					fetchSignUpWithFB(communityId, token, registerkey, undefined, lang)
				);
			} else {
				lastLoginAttemptRef.current = { kind: "facebook", token };
				dispatch(fetchLoginWithFB(communityId, token));
			}
		} catch (e) {
			if (isFacebookCancel(e)) {
				dispatch({
					type: SET_DROP_DOWN_ALERT_WARN,
					warn: "facebookwarn"
				});
			} else {
				dispatch({
					type: SET_DROP_DOWN_ALERT_ERROR,
					error: "facebookerror"
				});
			}
		}
	};

	const showTooltips = async () => {
		// const hideTooltips = await AsyncStorage.getItem("hideTooltips");
		const hideTooltips = true;
		if (!token && !hideTooltips && !(isAndroid && __DEV__)) {
			if (!needRegisterKey) {
				setTimeout(() => dispatch({ type: SET_TOOLTIP3_VISIBLE }), 1000);
			} else if (majorVersionIOS >= 13) {
				setTimeout(() => dispatch({ type: SET_TOOLTIP4_VISIBLE }), 1000);
			} else if (!isElectron) {
				setTimeout(() => dispatch({ type: SET_TOOLTIP5_VISIBLE }), 1000);
			}
		}
	};

	const dispatchSelectCategory = category => {
		Keyboard.dismiss();
		dispatch({
			type: SET_SELECTED_CATEGORY,
			value: category
		});
	};

	const login = () => {
		lastLoginAttemptRef.current = {
			kind: "local",
			emailOrPseudo,
			password
		};
		dispatch(fetchLogin(emailOrPseudo, password, communityId));
	};

	const signUp = () => {
		dispatch(fetchSignUp(emailOrPseudo, registerkey, communityId, lang));
	};

	const {
		color5,
		color2,
		darkgrey,
		white,
		bgColor1,
		bgTransparent,
		bgWhite,
		bgColor2,
		bgColor5,
		flex1,
		rounded10,
		rounded22,
		alignItemsCenter,
		justifyContentCenter,
		textCenter,
		fs20,
		fs10,
		h44,
		p40,
		row,
		pv20,
		font,
		fontLight,
		fontBold,
		ml10,
		opacity07,
		opacity1,
		underline,
		mb10
	} = commonStyles;

	const isLoginPage =
		communitytype !== "Neighborhood" || isPrivate || selectedCategory === 0;
	const isSignUpPage =
		communitytype === "Neighborhood" && !isPrivate && selectedCategory === 1;

	// Organizations and private communities only offer the login: the signup
	// category (selected from a neighborhood or the landing screen) would
	// validate the inputs as a signup (email required instead of email or pseudo)
	useEffect(() => {
		// communitytype is undefined while the community is loading
		if (
			selectedCategory &&
			communitytype &&
			(communitytype !== "Neighborhood" || isPrivate)
		) {
			dispatch({ type: SET_SELECTED_CATEGORY, value: 0 });
		}
	}, [selectedCategory, communitytype, isPrivate]);

	// Signup guide: the user can register with a social account or with an
	// email, displayed once when the signup page is opened
	useEffect(() => {
		if (!isSignUpPage) {
			setShowSignupGuide(false);
			return;
		}
		if (signupGuideShownRef.current) {
			return;
		}
		const timeout = setTimeout(() => {
			signupGuideShownRef.current = true;
			setShowSignupGuide(true);
		}, 500);
		return () => clearTimeout(timeout);
	}, [isSignUpPage]);

	// Social login icons displayed (copied over the guide overlay)
	const socialIcons = [
		isWeb && !socialLogin?.google?.hidden && <GoogleIcon size={26} />,
		isWeb && !socialLogin?.facebook?.hidden && <FacebookIcon size={28} />,
		(majorVersionIOS >= 13 || isWeb) && !socialLogin?.apple?.hidden && (
			<AppleIcon size={28} />
		)
	].filter(Boolean);

	const renderEmailInput = inputProps => (
		<Input
			leftIcon={
				isLoginPage ? (
					<FontAwesome
						name="id-card-o"
						color="rgba(0, 0, 0, 0.38)"
						size={22}
						style={{ backgroundColor: "transparent" }}
					/>
				) : (
					<FontAwesome
						name="envelope-o"
						color="rgba(0, 0, 0, 0.38)"
						size={25}
						style={{ backgroundColor: "transparent" }}
					/>
				)
			}
			value={emailOrPseudo}
			editable={!isFetching}
			keyboardAppearance="light"
			autoFocus={false}
			autoCapitalize="none"
			autoCorrect={false}
			keyboardType="email-address"
			returnKeyType={isSignUpPage ? "done" : "next"}
			inputStyle={[ml10, fontLight]}
			inputContainerStyle={{
				borderBottomColor: "rgba(68, 64, 65, 0.38)",
				borderBottomWidth: 1
			}}
			containerStyle={{
				marginTop: 10,
				width: SCREEN_WIDTH - 40
			}}
			placeholder={isLoginPage ? t("login:emailorpseudo") : t("login:email")}
			onSubmitEditing={() =>
				isLoginPage ? passwordInputRef?.current?.focus() : null
			}
			onChangeText={emailOrPseudo =>
				dispatch({
					type: SET_EMAIL_OR_PSEUDO,
					value: emailOrPseudo
				})
			}
			errorMessage={emailOrPseudoErrorMsg}
			{...inputProps}
		/>
	);

	const signupGuideSteps = [
		...(socialIcons.length
			? [
					{
						key: "social",
						anchorRef: socialAnchorRef,
						renderAnchor: () => (
							<View style={row}>
								{socialIcons.map((icon, index) => (
									<View key={index} style={socialCircleStyle}>
										{icon}
									</View>
								))}
							</View>
						),
						text: t("login:signupguidesocial"),
						placement: "top"
					}
			  ]
			: []),
		{
			key: "email",
			anchorRef: emailAnchorRef,
			renderAnchor: () => renderEmailInput({ editable: false }),
			text: socialIcons.length
				? t("login:signupguideemail")
				: t("login:signupguideemailonly"),
			placement: "bottom",
			width: 260,
			onPress: () => emailOrPseudoInputRef.current?.focus()
		}
	];

	return (
		<View style={[flex1, bgColor1]}>
			<ConfirmationModal
				ref={wrongNeighborhoodModalRef}
				title={t("login:wrongneighborhoodtitle")}
				description={t("login:wrongneighborhooddescription", {
					name: wrongNeighborhoodSuggestion?.name || "",
					name2: currentCommunity?.name || ""
				})}
				buttonConfirm={t("button:switchcommunity")}
				buttonColor={COLOR2}
				onConfirmation={async () => {
					const target = wrongNeighborhoodSuggestion;
					const attempt = lastLoginAttemptRef.current;
					dispatch({ type: CLEAR_WRONG_NEIGHBORHOOD_SUGGESTION });
					wrongNeighborhoodModalRef.current?.close();
					if (!target?._id) {
						return;
					}
					// Refresh the on-screen community so the user lands on the
					// right brand/logo before/after the auto-login completes.
					dispatch(
						fetchGetCommunityDetails(target.customUri || `${target._id}`)
					);
					// Replay the original strategy. On web the Google / Apple
					// authorization codes are one-shot (consumed by the first
					// server-side token exchange), so we must re-open the popup
					// and obtain a fresh code rather than re-sending the captured
					// one. The modal's button click is a valid user gesture for
					// re-opening the popup.
					try {
						switch (attempt?.kind) {
							case "google": {
								const credential = await signInWithGoogle();
								dispatch(fetchLoginWithGoogle(target._id, credential));
								break;
							}
							case "apple": {
								const credential = await signInWithApple();
								dispatch(fetchLoginWithApple(target._id, credential));
								break;
							}
							case "facebook": {
								// FB access tokens stay valid, so reuse without
								// re-opening the popup.
								dispatch(fetchLoginWithFB(target._id, attempt.token));
								break;
							}
							case "local":
							default: {
								const emailOrPseudoSafe =
									attempt?.emailOrPseudo ?? emailOrPseudo;
								const passwordSafe = attempt?.password ?? password;
								dispatch(
									fetchLogin(emailOrPseudoSafe, passwordSafe, target._id)
								);
							}
						}
					} catch (e) {
						// User cancelled the social-login popup. Stay on the new
						// community so they can retry from the login screen.
						if (attempt?.kind === "google" && isGoogleCancel(e)) {
							dispatch({ type: SET_DROP_DOWN_ALERT_WARN, warn: "googlewarn" });
						} else if (attempt?.kind === "apple" && isAppleCancel(e)) {
							dispatch({ type: SET_DROP_DOWN_ALERT_WARN, warn: "applewarn" });
						} else {
							dispatch({
								type: SET_DROP_DOWN_ALERT_ERROR,
								error:
									attempt?.kind === "google"
										? "googleerror"
										: attempt?.kind === "apple"
										? "appleerror"
										: "unexpectederror"
							});
						}
					}
				}}
				onClosed={() => dispatch({ type: CLEAR_WRONG_NEIGHBORHOOD_SUGGESTION })}
			/>
			<ConfirmationModal
				ref={registrationBlockedModalRef}
				title={t("login:registrationblockedtitle")}
				description={t("login:registrationblockeddescription", {
					name: registrationBlockedByCommunity?.name || "",
					name2: currentCommunity?.name || ""
				})}
				buttonConfirm={t("button:switchcommunity")}
				buttonColor={COLOR2}
				onConfirmation={() => {
					const target = registrationBlockedByCommunity;
					dispatch({ type: CLEAR_REGISTRATION_BLOCKED_BY_COMMUNITY });
					registrationBlockedModalRef.current?.close();
					if (target?._id) {
						// Switch to the login tab on the user's actual community: from
						// there they can sign in and leave the community manually via
						// their profile. We re-fetch the community directly (rather
						// than rely on a navigate to the same screen with new params,
						// which doesn't re-trigger the community fetch).
						dispatch({ type: SET_SELECTED_CATEGORY, value: 0 });
						dispatch(
							fetchGetCommunityDetails(target.customUri || `${target._id}`)
						);
					}
				}}
				onClosed={() =>
					dispatch({ type: CLEAR_REGISTRATION_BLOCKED_BY_COMMUNITY })
				}
			/>
			<SafeAreaView style={flex1}>
				<View style={flex1}>
					<TouchableOpacity
						style={styles.closeModal}
						onPress={() => navigation.goBack()}
					>
						<FontAwesome raised name="caret-down" size={40} color="#444041" />
					</TouchableOpacity>
					<KeyboardAvoidingView
						keyboardVerticalOffset={isiOS ? 0 : 500}
						style={[
							flex1,
							bgColor1,
							p40,
							alignItemsCenter,
							justifyContentCenter
						]}
						behavior={isiOS ? "padding" : undefined}
					>
						{showImage ? (
							logoUri ? (
								<Image
									style={{
										width: SCREEN_WIDTH / 3,
										height: SCREEN_WIDTH / 3,
										resizeMode: "contain",
										marginBottom:
											communitytype === "Neighborhood" && !isPrivate ? 20 : 40
									}}
									source={{ uri: logoUri }}
								/>
							) : (
								<Image
									style={{
										width: SCREEN_WIDTH / 3,
										height: SCREEN_WIDTH / 3,
										marginBottom:
											communitytype === "Neighborhood" && !isPrivate ? 20 : 40
									}}
									source={getImage("logo")}
								/>
							)
						) : (
							<Text
								style={[
									fontBold,
									color2,
									fs20,
									{
										marginBottom:
											communitytype === "Neighborhood" && !isPrivate ? 20 : 40
									}
								]}
							>
								{communityName}
							</Text>
						)}
						<View>
							{communitytype === "Neighborhood" && !isPrivate && (
								<>
									<View style={row}>
										<Button
											disabled={isFetching}
											clear
											// activeOpacity={0.7}
											onPress={() => dispatchSelectCategory(0)}
											containerStyle={flex1}
											titleStyle={[
												textCenter,
												color5,
												fs20,
												opacity07,
												isLoginPage && opacity1
											]}
											buttonStyle={[bgTransparent]}
											disabledStyle={[bgTransparent]}
											title={t("login:login")}
										/>
										<Button
											disabled={isFetching}
											clear
											// activeOpacity={0.7}
											onPress={() => dispatchSelectCategory(1)}
											containerStyle={flex1}
											titleStyle={[
												textCenter,
												color5,
												fs20,
												opacity07,
												isSignUpPage && opacity1
											]}
											buttonStyle={[bgTransparent]}
											disabledStyle={[bgTransparent]}
											title={t("login:register")}
										/>
									</View>

									<View style={[alignItemsCenter, row, { height: 20 }]}>
										<TabSelector selected={isLoginPage} />
										<TabSelector selected={isSignUpPage} />
									</View>
								</>
							)}
							<View
								style={[
									bgWhite,
									alignItemsCenter,
									pv20,
									rounded10,
									{ width: SCREEN_WIDTH - 30 }
								]}
							>
								<View style={[row, mb10]}>
									{!needRegisterKey &&
										communitytype === "Neighborhood" &&
										!isPrivate && (
											<Tooltip
												ref={tooltip3Ref}
												popover={
													<Text style={[font, white]}>
														{t("login:tooltip3")}
													</Text>
												}
												backgroundColor={DARKGREY_COLOR}
												toggleOnPress={false}
												withOverlay={true}
												height={80}
												width={180}
												onClose={() => {
													setTimeout(
														() =>
															majorVersionIOS >= 13
																? dispatch({
																		type: SET_TOOLTIP4_VISIBLE
																  })
																: !isElectron
																? dispatch({
																		type: SET_TOOLTIP5_VISIBLE
																  })
																: dispatch({
																		type: SET_TOOLTIP6_VISIBLE,
																		payload: true
																  }),
														10
													);
												}}
												skipAndroidStatusBar
											>
												<TouchableOpacity
													style={[
														bgColor5,
														alignItemsCenter,
														justifyContentCenter,
														{
															height: 54,
															width: 54,
															marginHorizontal: 10,
															borderRadius: 27
														}
													]}
													onPress={() =>
														isSignUpPage
															? dispatch({
																	type: CLEAR_VALIDATE_GUEST
															  })
															: loginAsGuest()
													}
													backgroundColor={DARKGREY_COLOR}
													toggleOnPress={false}
													withOverlay={true}
													height={80}
													width={180}
													onClose={() => {
														setTimeout(
															() =>
																majorVersionIOS >= 13
																	? dispatch({
																			type: SET_TOOLTIP4_VISIBLE
																	  })
																	: dispatch({
																			type: SET_TOOLTIP5_VISIBLE
																	  }),
															10
														);
													}}
													skipAndroidStatusBar
												>
													<Zocial name="guest" size={32} color={WHITE_COLOR} />
												</TouchableOpacity>
											</Tooltip>
										)}
									<View ref={socialAnchorRef} style={row} collapsable={false}>
										{isWeb && !socialLogin?.google?.hidden && (
											<TouchableOpacity
												style={socialCircleStyle}
												onPress={() => {
													// Google's popup must open from this user gesture, so call
													// the sign-in directly (no redux roundtrip). Block only when
													// a community key is required but missing.
													if (isSignUpPage && needRegisterKey && !registerkey) {
														dispatch({ type: CLEAR_VALIDATE_APPLE });
														return;
													}
													logInWithGoogle();
												}}
											>
												<GoogleIcon size={26} />
											</TouchableOpacity>
										)}
										{isWeb && !socialLogin?.facebook?.hidden && (
											<TouchableOpacity
												style={socialCircleStyle}
												onPress={() => {
													// Facebook's popup must open from this user gesture, so
													// call the sign-in directly. Block only when a community
													// key is required but missing.
													if (isSignUpPage && needRegisterKey && !registerkey) {
														dispatch({ type: CLEAR_VALIDATE_APPLE });
														return;
													}
													logInWithFB();
												}}
											>
												<FacebookIcon size={28} />
											</TouchableOpacity>
										)}
										{majorVersionIOS >= 13 && !socialLogin?.apple?.hidden && (
											<Tooltip
												ref={tooltip4Ref}
												popover={
													<Text style={[font, white]}>
														{needRegisterKey
															? t("login:tooltip4bis")
															: t("login:tooltip4")}
													</Text>
												}
												backgroundColor={DARKGREY_COLOR}
												toggleOnPress={false}
												withOverlay={true}
												height={80}
												width={180}
												onClose={() =>
													setTimeout(
														() =>
															dispatch({
																type: SET_TOOLTIP5_VISIBLE
															}),
														10
													)
												}
												skipAndroidStatusBar
											>
												<TouchableOpacity
													style={socialCircleStyle}
													onPress={() =>
														dispatch({
															type: CLEAR_VALIDATE_APPLE
														})
													}
												>
													<AppleIcon size={28} />
												</TouchableOpacity>
											</Tooltip>
										)}
										{isWeb && !socialLogin?.apple?.hidden && (
											<TouchableOpacity
												style={socialCircleStyle}
												onPress={() => {
													// Apple's popup must open from this user gesture, so
													// call the sign-in directly (no redux roundtrip). Block
													// only when a community key is required but missing.
													if (isSignUpPage && needRegisterKey && !registerkey) {
														dispatch({ type: CLEAR_VALIDATE_APPLE });
														return;
													}
													logInWithApple();
												}}
											>
												<AppleIcon size={28} />
											</TouchableOpacity>
										)}
									</View>
								</View>
								{((!needRegisterKey &&
									communitytype === "Neighborhood" &&
									!isPrivate) ||
									(majorVersionIOS >= 13 && !socialLogin?.apple?.hidden) ||
									(isWeb &&
										(!socialLogin?.google?.hidden ||
											!socialLogin?.facebook?.hidden ||
											!socialLogin?.apple?.hidden))) && (
									<Text style={[fontBold, darkgrey]}>{t("login:or")}</Text>
								)}
								<View ref={emailAnchorRef} collapsable={false}>
									<Tooltip
										ref={tooltip6Ref}
										popover={
											<Text style={[font, white]}>{t("login:tooltip6")}</Text>
										}
										backgroundColor={DARKGREY_COLOR}
										toggleOnPress={false}
										withOverlay={true}
										height={80}
										width={200}
										onClose={() =>
											setTimeout(
												() =>
													dispatch({
														type: SET_TOOLTIP6_VISIBLE,
														payload: false
													}),
												10
											)
										}
										skipAndroidStatusBar
									>
										{renderEmailInput({ ref: emailOrPseudoInputRef })}
									</Tooltip>
								</View>
								{isLoginPage && (
									<Input
										leftIcon={
											<SimpleLineIcons
												name="lock"
												color="rgba(0, 0, 0, 0.38)"
												size={25}
												style={{ backgroundColor: "transparent" }}
											/>
										}
										value={password}
										editable={!isFetching}
										keyboardAppearance="light"
										autoCapitalize="none"
										autoCorrect={false}
										secureTextEntry={true}
										returnKeyType={"done"}
										inputContainerStyle={{
											borderBottomColor: "rgba(68, 64, 65, 0.38)",
											borderBottomWidth: 1
										}}
										inputStyle={[ml10, fontLight]}
										containerStyle={{
											width: SCREEN_WIDTH - 40
										}}
										placeholder={t("login:password")}
										ref={passwordInputRef}
										onChangeText={password =>
											dispatch({
												type: SET_PASSWORD,
												value: password
											})
										}
										onSubmitEditing={() =>
											dispatch({
												type: CLEAR_VALIDATE
											})
										}
										errorMessage={tokenErrorMsg || passwordErrorMsg}
									/>
								)}
								{isSignUpPage && displayRegisterkey && (
									<Input
										leftIcon={
											<SimpleLineIcons
												name="lock"
												color="rgba(0, 0, 0, 0.38)"
												size={25}
												style={{ backgroundColor: "transparent" }}
											/>
										}
										value={registerkey}
										editable={!isFetching}
										keyboardAppearance="light"
										autoCapitalize="none"
										autoCorrect={false}
										secureTextEntry={true}
										returnKeyType={"done"}
										containerStyle={[
											emailOrPseudoErrorMsg
												? {
														marginTop: 10
												  }
												: {
														marginTop: 16
												  },
											{
												width: SCREEN_WIDTH - 40
											},
											mb10
										]}
										inputContainerStyle={{
											borderBottomColor: "rgba(68, 64, 65, 0.38)",
											borderBottomWidth: 1
										}}
										inputStyle={[ml10, fontLight]}
										placeholder={t("login:neighborhoodkey")}
										ref={registerkeyInputRef}
										onChangeText={registerkey =>
											dispatch({
												type: SET_REGISTER_KEY,
												value: registerkey
											})
										}
										errorMessage={registerkeyErrorMsg}
									/>
								)}
								{isLoginPage ? (
									<Button
										type="clear"
										titleStyle={[color5, fontBold, fs10, underline]}
										containerStyle={{ marginTop: 16 }}
										title={t("login:forgotpassword")}
										onPress={() =>
											dispatch({
												type: CLEAR_FORGET_PWD_VALIDATE
											})
										}
										disabled={isFetching || !canChangePassword}
									>
										{t("login:forgotpassword")}
									</Button>
								) : (
									<CheckBox
										title={t("login:cgu")}
										textStyle={[darkgrey, fs10, fontBold]}
										checkedColor={COLOR2}
										containerStyle={{ marginTop: 16 }}
										onPress={() => {
											dispatch({ type: TOGGLE_CGU_CHECKED });
											if (!cguChecked) {
												navigation.navigate("CGUScreen");
											}
										}}
										checked={cguChecked}
									/>
								)}
								<Button
									buttonStyle={[bgColor2, h44, rounded22, { width: 140 }]}
									containerStyle={[{ marginTop: 16 }]}
									activeOpacity={0.8}
									title={isLoginPage ? t("button:signin") : t("button:signup")}
									onPress={() =>
										dispatch({
											type: CLEAR_VALIDATE
										})
									}
									titleStyle={[white, fontBold, { fontSize: 16 }]}
									loading={isFetching}
									disabled={isFetching || (!cguChecked && isSignUpPage)}
								/>
							</View>
						</View>
					</KeyboardAvoidingView>
				</View>
			</SafeAreaView>
			<SelectionGuide
				visible={showSignupGuide && isSignUpPage}
				steps={signupGuideSteps}
				onClose={() => setShowSignupGuide(false)}
			/>
		</View>
	);
};

const LoginModal = memo(Login);
export default LoginModal;

const styles = StyleSheet.create({
	selected: {
		position: "absolute",
		borderRadius: 50,
		height: 0,
		width: 0,
		top: -5,
		borderRightWidth: 70,
		borderBottomWidth: 70,
		borderColor: "white",
		backgroundColor: "white"
	},
	closeModal: {
		position: "absolute",
		top: 10,
		width: "100%",
		alignItems: "center",
		zIndex: 2
	}
});
